import React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Search, Trash2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export type UnifiedTableAlign = "left" | "center" | "right";
export type UnifiedTableVariant = "default" | "striped" | "compact" | "bordered";
export type UnifiedTableDensity = "comfortable" | "compact";
export type UnifiedTableValueType = "auto" | "text" | "date" | "number" | "boolean";
export type UnifiedTableDataType =
  | "auto"
  | "string"
  | "text"
  | "number"
  | "int"
  | "float"
  | "decimal"
  | "date"
  | "datetime"
  | "timestamp"
  | "boolean"
  | "bool";

export interface UnifiedTableColumn<T extends object> {
  key: string;
  header: React.ReactNode;
  field?: keyof T;
  accessor?: (row: T) => React.ReactNode;
  searchValue?: (row: T) => unknown;
  sortValue?: (row: T) => unknown;
  sortable?: boolean;
  align?: UnifiedTableAlign;
  width?: string;
  className?: string;
  headerClassName?: string;
  wrap?: boolean;
  wrapAfterChars?: number;
  preserveNewlines?: boolean;
  truncate?: boolean;
  truncateAfterChars?: number;
  truncateSuffix?: string;
  dataType?: UnifiedTableDataType;
  valueType?: UnifiedTableValueType;
}

interface UnifiedTableProps<T extends object> {
  columns: UnifiedTableColumn<T>[];
  data: T[];
  rowKey?: (row: T, index: number) => string;
  getRowClassName?: (row: T, index: number) => string | undefined;
  getRowStyle?: (row: T, index: number) => React.CSSProperties | undefined;
  searchEnabled?: boolean;
  clientSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  loading?: boolean;
  loadingRows?: number;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  variant?: UnifiedTableVariant;
  density?: UnifiedTableDensity;
  className?: string;
  initialSortKey?: string;
  initialSortDirection?: "asc" | "desc";
  sortKey?: string;
  sortDirection?: "asc" | "desc";
  clientSort?: boolean;
  onSortChange?: (key: string, direction: "asc" | "desc") => void;
  rowActionsHeader?: React.ReactNode;
  renderRowActions?: (row: T, index: number) => React.ReactNode;
  stickyRowActions?: boolean;
  showRowActions?: boolean;
  onEditRow?: (row: T, index: number) => void;
  onDeleteRow?: (row: T, index: number) => void;
  editActionLabel?: string;
  deleteActionLabel?: string;
  searchAlign?: "left" | "right";
  searchButtonLabel?: string;
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T\s].+/;
const TIME_PATTERN = /\b\d{1,2}:\d{2}(:\d{2})?\b/;
const TZ_PATTERN = /(GMT|UTC|[+-]\d{2}:?\d{2}|Z)/i;
const TEXTUAL_DATE_PATTERN = /[a-zA-Z]{3,}/;

const padTwo = (value: number): string => String(value).padStart(2, "0");

const isDateTimeDataType = (dataType?: UnifiedTableDataType): boolean =>
  dataType === "datetime" || dataType === "timestamp";

const normalizeDataType = (
  dataType?: UnifiedTableDataType,
  valueType?: UnifiedTableValueType
): UnifiedTableValueType => {
  if (dataType) {
    if (dataType === "date") {
      return "date";
    }

    if (dataType === "datetime") {
      return "date";
    }

    if (dataType === "timestamp") {
      return "date";
    }

    if (dataType === "number") {
      return "number";
    }

    if (dataType === "int") {
      return "number";
    }

    if (dataType === "float") {
      return "number";
    }

    if (dataType === "decimal") {
      return "number";
    }

    if (dataType === "boolean") {
      return "boolean";
    }

    if (dataType === "bool") {
      return "boolean";
    }

    if (dataType === "string") {
      return "text";
    }

    if (dataType === "text") {
      return "text";
    }
  }

  if (valueType && valueType !== "auto") {
    return valueType;
  }

  return "auto";
};

const isDateLikeValue = (value: unknown): boolean => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return true;
  }

  if (DATE_TIME_PATTERN.test(trimmed) && !Number.isNaN(Date.parse(trimmed))) {
    return true;
  }

  if (!TEXTUAL_DATE_PATTERN.test(trimmed) && !trimmed.includes("/") && !trimmed.includes("-")) {
    return false;
  }

  const epoch = Date.parse(trimmed);
  if (!Number.isNaN(epoch)) {
    return true;
  }

  return false;
};

const parseDateToEpoch = (value: unknown): number | null => {
  if (value instanceof Date) {
    const epoch = value.getTime();
    if (!Number.isNaN(epoch)) {
      return epoch;
    }
    return null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map((part) => Number(part));
    return Date.UTC(year, month - 1, day);
  }

  if (DATE_TIME_PATTERN.test(trimmed)) {
    const epoch = Date.parse(trimmed);
    if (!Number.isNaN(epoch)) {
      return epoch;
    }
  }

  const fallbackEpoch = Date.parse(trimmed);
  if (!Number.isNaN(fallbackEpoch)) {
    return fallbackEpoch;
  }

  return null;
};

const formatDateValue = (value: unknown, dataType?: UnifiedTableDataType): string => {
  const toDateToken = (input: unknown): string | null => {
    if (typeof input === "string") {
      const trimmed = input.trim();

      if (DATE_ONLY_PATTERN.test(trimmed)) {
        return trimmed;
      }

      const leading = trimmed.slice(0, 10);
      if (DATE_ONLY_PATTERN.test(leading)) {
        return leading;
      }
    }

    if (input instanceof Date) {
      const epoch = input.getTime();
      if (!Number.isNaN(epoch)) {
        return `${input.getUTCFullYear()}-${padTwo(input.getUTCMonth() + 1)}-${padTwo(input.getUTCDate())}`;
      }
    }

    const epoch = parseDateToEpoch(input);
    if (epoch === null) {
      return null;
    }

    return new Date(epoch).toISOString().slice(0, 10);
  };

  if (dataType === "date") {
    const token = toDateToken(value);
    if (!token) {
      return "";
    }

    const [year, month, day] = token.split("-");
    return `${month}/${day}/${year}`;
  }

  const epoch = parseDateToEpoch(value);
  if (epoch === null) {
    return "";
  }

  const date = new Date(epoch);
  const normalizedString = typeof value === "string" ? value.trim() : "";

  const containsTime =
    isDateTimeDataType(dataType) ||
    ((!dataType || dataType === "auto") &&
      normalizedString.length > 0 &&
      TIME_PATTERN.test(normalizedString) &&
      (DATE_TIME_PATTERN.test(normalizedString) || TZ_PATTERN.test(normalizedString)));

  if (containsTime) {
    return date.toLocaleString();
  }

  return date.toLocaleDateString();
};

const parseBooleanValue = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }

    if (value === 0) {
      return false;
    }

    return null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (["1", "true", "t", "yes", "y", "on"].includes(normalized)) {
    return true;
  }

  if (["0", "false", "f", "no", "n", "off"].includes(normalized)) {
    return false;
  }

  return null;
};

const inferValueType = (
  value: unknown,
  dataType?: UnifiedTableDataType,
  valueType?: UnifiedTableValueType
): UnifiedTableValueType => {
  const normalizedType = normalizeDataType(dataType, valueType);
  if (normalizedType !== "auto") {
    return normalizedType;
  }

  if (isDateLikeValue(value)) {
    return "date";
  }

  if (typeof value === "number") {
    return "number";
  }

  if (typeof value === "boolean") {
    return "boolean";
  }

  return "text";
};

const toSearchText = (
  value: unknown,
  dataType?: UnifiedTableDataType,
  valueType?: UnifiedTableValueType
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValueType = inferValueType(value, dataType, valueType);

  if (normalizedValueType === "date") {
    return formatDateValue(value, dataType);
  }

  if (normalizedValueType === "boolean") {
    const normalizedBoolean = parseBooleanValue(value);
    if (normalizedBoolean === true) {
      return "Yes";
    }

    if (normalizedBoolean === false) {
      return "No";
    }
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "boolean") {
    return String(value);
  }

  if (React.isValidElement(value)) {
    return "";
  }

  return String(value);
};

const toComparableValue = (
  value: unknown,
  dataType?: UnifiedTableDataType,
  valueType?: UnifiedTableValueType
): string | number => {
  const normalizedValueType = inferValueType(value, dataType, valueType);

  if (normalizedValueType === "date") {
    const epoch = parseDateToEpoch(value);
    if (epoch !== null) {
      return epoch;
    }
  }

  if (normalizedValueType === "number" && typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (normalizedValueType === "boolean") {
    const normalizedBoolean = parseBooleanValue(value);
    if (normalizedBoolean === true) {
      return 1;
    }

    if (normalizedBoolean === false) {
      return 0;
    }
  }

  if (typeof value === "number") {
    return value;
  }

  return toSearchText(value, dataType, valueType).toLowerCase();
};

const toRenderableValue = (
  value: unknown,
  dataType?: UnifiedTableDataType,
  valueType?: UnifiedTableValueType
): React.ReactNode => {
  if (React.isValidElement(value)) {
    return value;
  }

  const normalizedValueType = inferValueType(value, dataType, valueType);

  if (normalizedValueType === "date") {
    const formatted = formatDateValue(value, dataType);
    if (formatted) {
      return formatted;
    }
  }

  if (normalizedValueType === "boolean") {
    const normalizedBoolean = parseBooleanValue(value);
    if (normalizedBoolean === true) {
      return "Yes";
    }

    if (normalizedBoolean === false) {
      return "No";
    }
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const getAlignClass = (align: UnifiedTableAlign | undefined): string => {
  if (align === "center") {
    return "text-center";
  }

  if (align === "right") {
    return "text-right";
  }

  return "text-left";
};

const getColumnValue = <T extends object>(
  row: T,
  column: UnifiedTableColumn<T>,
  mode: "display" | "search" | "sort"
): unknown => {
  if (mode === "search" && column.searchValue) {
    return column.searchValue(row);
  }

  if (mode === "sort" && column.sortValue) {
    return column.sortValue(row);
  }

  if (mode === "display" && column.accessor) {
    return column.accessor(row);
  }

  if (column.field) {
    return row[column.field];
  }

  if (column.accessor) {
    return column.accessor(row);
  }

  return "";
};

export function UnifiedTable<T extends object>({
  columns,
  data,
  rowKey,
  getRowClassName,
  getRowStyle,
  searchEnabled = false,
  clientSearch = true,
  searchPlaceholder = "Search records...",
  searchValue,
  onSearchValueChange,
  onSearchSubmit,
  loading = false,
  loadingRows = 6,
  emptyTitle = "No records found",
  emptyDescription = "Try changing search terms or filters.",
  emptyAction,
  toolbarLeft,
  toolbarRight,
  variant = "striped",
  density = "compact",
  className,
  initialSortKey,
  initialSortDirection = "asc",
  sortKey,
  sortDirection,
  clientSort = true,
  onSortChange,
  rowActionsHeader = "Actions",
  renderRowActions,
  stickyRowActions = false,
  showRowActions = false,
  onEditRow,
  onDeleteRow,
  editActionLabel = "Edit",
  deleteActionLabel = "Delete",
  searchAlign = "left",
  searchButtonLabel = "Search",
}: UnifiedTableProps<T>) {
  const firstSortableColumn = React.useMemo(
    () => columns.find((column) => column.sortable),
    [columns]
  );

  const [internalSearchValue, setInternalSearchValue] = React.useState("");
  const [internalSortKey, setInternalSortKey] = React.useState(
    initialSortKey || firstSortableColumn?.key || ""
  );
  const [internalSortDirection, setInternalSortDirection] = React.useState<"asc" | "desc">(
    initialSortDirection
  );

  const activeSearchValue = searchValue ?? internalSearchValue;
  const normalizedSearchValue = activeSearchValue.trim().toLowerCase();
  const activeSortKey = sortKey ?? internalSortKey;
  const activeSortDirection = sortDirection ?? internalSortDirection;
  const hasRowActions = showRowActions && Boolean(renderRowActions || onEditRow || onDeleteRow);
  const rowActionsHeaderClass = stickyRowActions
    ? "sticky right-0 top-0 z-20 bg-gray-50 shadow-[-1px_0_0_0_rgba(229,231,235,1)]"
    : "sticky top-0 z-10 bg-gray-50";
  const rowActionsCellClass = stickyRowActions
    ? "sticky right-0 z-10 bg-inherit shadow-[-1px_0_0_0_rgba(229,231,235,1)]"
    : "";

  const filteredRows = React.useMemo(() => {
    if (!searchEnabled || !clientSearch) {
      return data;
    }

    if (!normalizedSearchValue) {
      return data;
    }

    return data.filter((row) =>
      columns.some((column) => {
        const value = getColumnValue(row, column, "search");
        return toSearchText(value, column.dataType, column.valueType)
          .toLowerCase()
          .includes(normalizedSearchValue);
      })
    );
  }, [clientSearch, columns, data, normalizedSearchValue, searchEnabled]);

  const sortedRows = React.useMemo(() => {
    if (!clientSort) {
      return filteredRows;
    }

    if (!activeSortKey) {
      return filteredRows;
    }

    const selectedColumn = columns.find((column) => column.key === activeSortKey && column.sortable);
    if (!selectedColumn) {
      return filteredRows;
    }

    const directionFactor = activeSortDirection === "asc" ? 1 : -1;
    return [...filteredRows].sort((leftRow, rightRow) => {
      const leftComparable = toComparableValue(
        getColumnValue(leftRow, selectedColumn, "sort"),
        selectedColumn.dataType,
        selectedColumn.valueType
      );
      const rightComparable = toComparableValue(
        getColumnValue(rightRow, selectedColumn, "sort"),
        selectedColumn.dataType,
        selectedColumn.valueType
      );

      if (leftComparable === rightComparable) {
        return 0;
      }

      return leftComparable > rightComparable ? directionFactor : -directionFactor;
    });
  }, [activeSortDirection, activeSortKey, clientSort, columns, filteredRows]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    onSearchValueChange?.(nextValue);

    if (searchValue !== undefined) {
      return;
    }

    setInternalSearchValue(nextValue);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    onSearchSubmit?.(activeSearchValue);
  };

  const handleSort = (column: UnifiedTableColumn<T>) => {
    if (!column.sortable) {
      return;
    }

    const nextDirection =
      activeSortKey !== column.key ? "asc" : activeSortDirection === "asc" ? "desc" : "asc";

    if (onSortChange) {
      onSortChange(column.key, nextDirection);
      return;
    }

    setInternalSortKey(column.key);
    setInternalSortDirection(nextDirection);
  };

  const renderSortIcon = (column: UnifiedTableColumn<T>) => {
    if (!column.sortable) {
      return null;
    }

    if (activeSortKey !== column.key) {
      return <ArrowUpDown className="h-4 w-4 text-[var(--neutral-gray-90)]" />;
    }

    if (activeSortDirection === "asc") {
      return <ArrowUp className="h-4 w-4 text-[var(--azure-primary)]" />;
    }

    return <ArrowDown className="h-4 w-4 text-[var(--azure-primary)]" />;
  };

  const headerCellPadding = density === "compact" ? "py-1 px-2.5 h-8" : "py-1.5 px-3 h-9";
  const bodyCellPadding = density === "compact" ? "py-1 px-2.5" : "py-1.5 px-3";

  const rowVariantClass = React.useMemo(() => {
    if (variant === "striped") {
      return "border-[var(--neutral-gray-30)] odd:bg-[var(--surface-muted)] even:bg-[var(--surface-panel-solid)] hover:bg-[var(--surface-muted)]";
    }

    if (variant === "bordered") {
      return "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] hover:bg-[var(--surface-muted)]";
    }

    return "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] hover:bg-[var(--surface-muted)]";
  }, [variant]);

  const tableOuterClass = cn(
    "flex w-full flex-col rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)]",
    variant === "bordered" &&
      "[&_th]:border-r [&_td]:border-r [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_th]:border-[var(--neutral-gray-30)] [&_td]:border-[var(--neutral-gray-30)]",
    className,
    // The inner wrapper owns scrolling so the outer container does not scroll.
    "overflow-hidden"
  );

  const renderSearch = () => {
    if (!searchEnabled) {
      return null;
    }

    const clearSearch = () => {
      onSearchValueChange?.("");
      if (searchValue === undefined) {
        setInternalSearchValue("");
      }
      onSearchSubmit?.("");
    };

    return (
      <div className="flex items-center">
        <div className="group relative flex h-8 w-64 items-center overflow-hidden rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-[var(--surface-panel-solid)] focus-within:border-[var(--azure-primary)] focus-within:ring-1 focus-within:ring-[var(--azure-primary)] transition-all">
          <Search className="ml-2.5 h-3.5 w-3.5 shrink-0 text-[var(--neutral-gray-90)] pointer-events-none group-focus-within:text-[var(--azure-primary)]" />
          <input
            value={activeSearchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className="h-full w-full border-none bg-transparent px-2 text-xs text-[var(--neutral-gray-120)] placeholder:text-[var(--neutral-gray-100)] focus:outline-none"
            aria-label="Search table rows"
          />
          {activeSearchValue && (
            <button
              type="button"
              onClick={clearSearch}
              className="flex h-8 w-8 shrink-0 items-center justify-center text-[var(--neutral-gray-90)] hover:bg-[var(--neutral-gray-20)] hover:text-[var(--neutral-gray-110)] transition-colors"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {onSearchSubmit && (
            <button
              type="button"
              onClick={() => onSearchSubmit(activeSearchValue)}
              className="h-full shrink-0 border-l border-[var(--neutral-gray-50)] bg-[var(--surface-muted)] px-3 text-xs font-medium text-[var(--neutral-gray-110)] hover:bg-[var(--neutral-gray-20)] transition-colors"
            >
              {searchButtonLabel}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full space-y-2">
      {(searchEnabled || toolbarLeft || toolbarRight) && (
        <div className="flex flex-wrap items-center gap-2">
          {searchAlign === "left" && renderSearch()}
          {toolbarLeft && <div className="flex items-center gap-2">{toolbarLeft}</div>}

          <div className="ml-auto flex items-center gap-2">
            {searchAlign === "right" && renderSearch()}
            {toolbarRight && <div className="flex items-center gap-2">{toolbarRight}</div>}
          </div>
        </div>
      )}

      <div className={tableOuterClass}>
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-max min-w-full caption-bottom text-xs">
            <TableHeader className="bg-[var(--surface-muted)]">
              <TableRow className="border-b border-[var(--neutral-gray-30)]">
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={cn(
                      headerCellPadding,
                      "sticky top-0 z-10 bg-[var(--surface-muted)]",
                      "whitespace-nowrap font-semibold text-[var(--neutral-gray-110)]",
                      getAlignClass(column.align),
                      column.headerClassName
                    )}
                    style={column.width ? { width: column.width } : undefined}
                  >
                    <button
                      type="button"
                      className={cn(
                        "inline-flex w-full items-center gap-2",
                        getAlignClass(column.align),
                        column.align === "right" && "justify-end",
                        column.align === "center" && "justify-center",
                        !column.align && "justify-start",
                        !column.sortable && "cursor-default"
                      )}
                      onClick={() => handleSort(column)}
                      disabled={!column.sortable}
                    >
                      <span>{column.header}</span>
                      {renderSortIcon(column)}
                    </button>
                  </TableHead>
                ))}
  
                {hasRowActions && (
                  <TableHead
                    className={cn(
                      headerCellPadding,
                      rowActionsHeaderClass,
                      "whitespace-nowrap font-semibold text-[var(--neutral-gray-110)]"
                    )}
                  >
                    {rowActionsHeader}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
  
            <TableBody>
              {loading &&
                Array.from({ length: loadingRows }).map((_, rowIndex) => (
                  <TableRow key={`loading-row-${rowIndex}`} className={rowVariantClass}>
                    {columns.map((column) => (
                      <TableCell
                        key={`loading-cell-${column.key}-${rowIndex}`}
                        className={cn(bodyCellPadding, getAlignClass(column.align))}
                      >
                        <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                      </TableCell>
                    ))}
  
                    {hasRowActions && (
                      <TableCell className={cn(bodyCellPadding, rowActionsCellClass)}>
                        <div className="ml-auto h-4 w-28 animate-pulse rounded bg-gray-200" />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
  
              {!loading && sortedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + (hasRowActions ? 1 : 0)} className="px-4 py-8 text-center">
                    <div className="space-y-2">
                      <p className="text-base font-medium text-[var(--neutral-gray-110)]">{emptyTitle}</p>
                      <p className="text-sm text-[var(--neutral-gray-100)]">{emptyDescription}</p>
                      {emptyAction && <div className="pt-2">{emptyAction}</div>}
                    </div>
                  </TableCell>
                </TableRow>
              )}
  
              {!loading &&
                sortedRows.map((row, rowIndex) => (
                  <TableRow
                    key={rowKey ? rowKey(row, rowIndex) : String(rowIndex)}
                    className={cn(rowVariantClass, getRowClassName?.(row, rowIndex))}
                    style={getRowStyle?.(row, rowIndex)}
                  >
                    {columns.map((column) => {
                      const rawValue = getColumnValue(row, column, "display");
                      const cellValue = toRenderableValue(rawValue, column.dataType, column.valueType);
  
                      const wrapAfterChars = column.wrapAfterChars ?? 32;
                      const preserveNewlines = Boolean(column.preserveNewlines);
                      const forceWrap = Boolean(column.wrap);
                      const truncateAfterChars = column.truncateAfterChars ?? 64;
                      const truncateSuffix = column.truncateSuffix ?? "...";
                      const shouldTruncate = column.truncate !== false;
  
                      let primitiveText = "";
                      if (typeof cellValue === "string") {
                        primitiveText = cellValue;
                      }
                      if (typeof cellValue === "number") {
                        primitiveText = String(cellValue);
                      }
  
                      let displayValue: React.ReactNode = cellValue;
                      let displayText = primitiveText;
  
                      if (shouldTruncate && primitiveText && primitiveText.length > truncateAfterChars) {
                        const clipped = primitiveText.slice(0, truncateAfterChars);
                        displayText = `${clipped}${truncateSuffix}`;
                        if (typeof cellValue === "string") {
                          displayValue = displayText;
                        }
                        if (typeof cellValue === "number") {
                          displayValue = displayText;
                        }
                      }
  
                      const shouldWrap = preserveNewlines || forceWrap || displayText.length > wrapAfterChars;
  
                      let whitespaceClass = "whitespace-nowrap";
                      if (shouldWrap) {
                        whitespaceClass = "whitespace-normal";
                      }
                      if (preserveNewlines) {
                        whitespaceClass = "whitespace-pre-wrap";
                      }
  
                      return (
                        <TableCell
                          key={`${column.key}-${rowIndex}`}
                          className={cn(
                            bodyCellPadding,
                            getAlignClass(column.align),
                            "text-[var(--neutral-gray-120)]",
                            column.className
                          )}
                        >
                          <div
                            className={cn(whitespaceClass, shouldWrap && "break-words")}
                            style={shouldWrap ? { maxWidth: `${wrapAfterChars}ch` } : undefined}
                          >
                            {displayValue}
                          </div>
                        </TableCell>
                      );
                    })}
  
                    {hasRowActions && (
                      <TableCell className={cn(bodyCellPadding, rowActionsCellClass)}>
                        {renderRowActions && renderRowActions(row, rowIndex)}
  
                        {!renderRowActions && (onEditRow || onDeleteRow) && (
                          <div className="flex items-center justify-end gap-2">
                            {onEditRow && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onEditRow(row, rowIndex)}
                                className="gap-1.5"
                              >
                                <Pencil className="h-4 w-4" />
                                {editActionLabel}
                              </Button>
                            )}
  
                            {onDeleteRow && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => onDeleteRow(row, rowIndex)}
                                className="gap-1.5"
                              >
                                <Trash2 className="h-4 w-4" />
                                {deleteActionLabel}
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>
      </div>
    </div>
  );
}
